import React, { useEffect, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Snackbar } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import Color from 'color';

import colors from 'config/colors';
import { useAppDispatch } from 'store/hooks';
import { OrderThunks } from 'store/Orders';
import { Text } from 'components';

import { styles } from './styles';
import type { StyleProp, ViewStyle } from 'react-native';
import type { Order, OrderItem } from 'store/Orders/types';

interface ReprintButtonProps {
  error?: string | null;
  setError?: React.Dispatch<React.SetStateAction<ReprintButtonProps['error']>>;
  status?: boolean;
  setStatus?: React.Dispatch<
    React.SetStateAction<ReprintButtonProps['status']>
  >;
  onPress?: () => void | Promise<void>;
  labelKey?: string;
  style?: StyleProp<ViewStyle>;
}

function ReprintButton({
  onPress,
  error,
  setError,
  status,
  setStatus,
  style,
  labelKey,
}: ReprintButtonProps) {
  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => {
        setStatus?.(false);
      }, 2000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [setStatus, status]);

  const accentColor = Color(colors.ui.statuses.prepared).darken(0.3).hex();

  return (
    <>
      <TouchableOpacity
        onPress={(e) => {
          e.stopPropagation();
          e.preventDefault();

          if (!status) {
            onPress();
          }
        }}
        activeOpacity={status ? 0.4 : 0.75}
        style={[
          styles.container,
          { flexGrow: 0, minWidth: 76 },
          status ? { opacity: 0.4 } : {},
          style,
        ]}
      >
        {status ? (
          <MaterialCommunityIcons
            name="printer-check"
            color={accentColor}
            size={30}
          />
        ) : (
          <MaterialCommunityIcons
            name="printer"
            color={accentColor}
            size={30}
          />
        )}
        <Text
          font={'bold'}
          color={accentColor}
          id={
            status
              ? 'common.actionButton.printing'
              : labelKey || 'common.actionButton.reprint'
          }
          size={12}
          align="center"
        />
      </TouchableOpacity>
      <Snackbar
        visible={Boolean(error)}
        duration={5000}
        onDismiss={() => setError?.(null)}
        style={styles.snackbar}
      >
        <Text
          id={`errors.${error}`}
          numberOfLines={3}
          style={styles.snackbarText}
        />
      </Snackbar>
    </>
  );
}

type ReprintButtonItemProps = ReprintButtonProps & {
  itemUUID: OrderItem['uuid'];
};

export const ReprintButtonItem = ({
  itemUUID,
  onPress: onPressProp,
}: ReprintButtonItemProps) => {
  const dispatch = useAppDispatch();

  const [status, setStatus] = useState<boolean>(false);
  const [error, setError] = useState<unknown | null>(null);

  const onPress = async () => {
    setStatus(true);

    const { payload } = await dispatch(
      OrderThunks.printItem({
        id: itemUUID,
      }),
    );

    if (payload?.error) {
      setError(payload.error);
    }

    onPressProp?.();
  };

  return (
    <ReprintButton
      style={{ width: '100%' }}
      onPress={onPress}
      status={status}
      setStatus={setStatus}
      error={error}
      setError={setError}
    />
  );
};

type ReprintButtonOrderProps = ReprintButtonProps & {
  order: Order;
  itemUUIDs: OrderItem['uuid'][];
};

export const ReprintButtonOrder = ({
  order,
  itemUUIDs,
  onPress: onPressProp,
}: ReprintButtonOrderProps) => {
  const dispatch = useAppDispatch();

  const [status, setStatus] = useState<boolean>(false);
  const [error, setError] = useState<unknown | null>(null);

  const onPress = async () => {
    setStatus(true);

    const { payload } = await dispatch(
      OrderThunks.printOrder({
        id: order.uuid,
        itemUUIDs,
      }),
    );

    if (payload?.error) {
      setError(payload.error);
    }

    onPressProp?.();
  };

  return (
    <ReprintButton
      onPress={onPress}
      status={status}
      setStatus={setStatus}
      error={error}
      setError={setError}
    />
  );
};

type ReprintMasterLabelButtonProps = ReprintButtonProps & {
  orderUUID: Order['uuid'];
};

export const ReprintMasterLabelButton = ({
  orderUUID,
  onPress: onPressProp,
  style,
}: ReprintMasterLabelButtonProps) => {
  const dispatch = useAppDispatch();

  const [status, setStatus] = useState<boolean>(false);
  const [error, setError] = useState<unknown | null>(null);

  const onPress = async () => {
    setStatus(true);

    const { payload } = await dispatch(
      OrderThunks.reprintMasterLabel({
        id: orderUUID,
      }),
    );

    if (payload?.error) {
      setError(payload?.error);
    }

    if (onPressProp) {
      onPressProp();
    }
  };

  return (
    <ReprintButton
      style={style}
      onPress={onPress}
      status={status}
      setStatus={setStatus}
      error={error}
      setError={setError}
      labelKey={'common.actionButton.reprintMasterLabel'}
    />
  );
};
